import React, {
	useContext,
	useEffect,
	useCallback,
	useMemo,
	useRef,
	useImperativeHandle,
} from 'react';
import { fg } from '@atlaskit/platform-feature-flags';

type OnHighlightChanged = (isHighlighted: boolean) => void;
type OnKeyDown = (e: KeyboardEvent, target: HTMLElement) => void;
export type RegisterKeyboardEnabled<U extends HTMLElement> = (
	currentId: number | null,
	domRef: U,
	onHighlightChanged: OnHighlightChanged,
	onKeyDownWhenHighlighted: OnKeyDown | undefined,
	navigationTrackingId: string | null,
	onHighlightedCallback?: (index: number) => void,
) => number;

interface KeyboardContextProps<U extends HTMLElement = HTMLElement> {
	registerKeyboardEnabled?: RegisterKeyboardEnabled<U>;
	unregisterKeyboardEnabled?: (ref: number) => void;
	getSelectedIndex?: () => number | undefined;
	highlightComponentAt?: (index: number) => void;
	unhighlightComponentAt?: (index: number) => void;
	unhighlightAll?: () => void;
	keyboardNavComponents?: KeyboardNavComponent[];
}

/**
 * The context's TypeScript type is `KeyboardContextProps<HTMLElement> | undefined`
 * so that callers of {@link useKeyboardHighlightProvider} are forced to guard
 * against the "no provider" case. Class component particularly returns undefined during SSR
 */
export const KeyboardHighlightContext: React.Context<
	KeyboardContextProps<HTMLElement> | undefined
> = React.createContext<KeyboardContextProps<HTMLElement> | undefined>({
	registerKeyboardEnabled: undefined,
	unregisterKeyboardEnabled: undefined,
	getSelectedIndex: undefined,
	highlightComponentAt: undefined,
	unhighlightComponentAt: undefined,
	unhighlightAll: undefined,
	keyboardNavComponents: [],
});

export interface KeyboardNavigationHookOptions {
	onKeydownCallback: OnKeyDown;
	onHighlightedCallback?: (index: number) => void;
	navigationTrackingId?: string | null;
}

export const useKeyboardNavigation: <U extends HTMLElement>(
	options?: KeyboardNavigationHookOptions,
) => [boolean, (ref: U) => void] = <U extends HTMLElement>(
	options?: KeyboardNavigationHookOptions,
): [boolean, (ref: U) => void] => {
	const [ref, setRef] = React.useState<U | null>(null);
	const registrationId = React.useRef<number | null>(null);
	const navigationTrackingId = options?.navigationTrackingId ?? null;
	const { registerKeyboardEnabled, unregisterKeyboardEnabled } =
		useContext(KeyboardHighlightContext) ?? {};
	const [isHighlighted, setIsHighlighted] = React.useState(false);
	const onKeydownCallback = options?.onKeydownCallback;
	const onHighlightedCallback = options?.onHighlightedCallback;

	const unregister = useCallback(() => {
		if (registrationId.current && unregisterKeyboardEnabled) {
			// If the ref got removed or changed we unregister the current ref
			unregisterKeyboardEnabled(registrationId.current);
		}
	}, [unregisterKeyboardEnabled]);

	// Will unmount effect
	useEffect(() => () => unregister(), [unregister]);

	// Ref change to null
	useEffect(() => {
		!ref && unregister();
	}, [ref, unregister]);

	// Everything else
	useEffect(() => {
		if (!registerKeyboardEnabled || !unregisterKeyboardEnabled) {
			return;
		}

		if (!ref) {
			return;
		}

		// Then we register the new ref
		if (ref) {
			// Another check here to ensure that the ref we get is actually a DOM node, there's probably better
			// ways to make this check but it doesn't matter too much.
			// If what we get is NOT a DOM node we will log a warning and default back to use React.findDOMNode
			if (!ref.compareDocumentPosition || !ref.addEventListener || !ref.removeEventListener) {
				// eslint-disable-next-line
				console.error(
					'It is expected that any keyboard highlighting enabled component also passes the ref all the way to the DOM node.',
					'It appears that the `ref` is not fowarded all the way to the DOM node, please use `React.forwardRef to ensure this is so`',
					'Falling back to using ReactDOM.findDOMNode, this is not recommended and this fallback may be removed in the future',
				);

				return;
			}

			registrationId.current = registerKeyboardEnabled(
				registrationId.current,
				ref,
				setIsHighlighted,
				onKeydownCallback,
				navigationTrackingId,
				onHighlightedCallback,
			);
		}
	}, [
		ref,
		setIsHighlighted,
		onKeydownCallback,
		registerKeyboardEnabled,
		unregisterKeyboardEnabled,
		navigationTrackingId,
		onHighlightedCallback,
	]);

	return [isHighlighted, setRef];
};

export interface KeyboardHighlightProviderProps {
	children?: React.ReactNode;
	listenerNode: HTMLElement | undefined | null;
}

export interface KeyboardNavComponent {
	id: number;
	domRef: HTMLElement;
	onHighlightChanged: OnHighlightChanged;
	onKeyDownWhenHighlighted: OnKeyDown | undefined;
	onHighlightedCallback?: (index: number) => void;
	navigationTrackingId: string | null;
}

/**
 * The imperative handle exposed by `KeyboardHighlightProvider` via `ref`.
 * Use this as the type argument for `React.MutableRefObject` or `React.RefObject`
 * when holding a ref to `KeyboardHighlightProvider` (replaces the old class-instance type).
 */
export interface KeyboardHighlightProviderHandle {
	highlightComponentAt: (index: number) => void;
	unhighlightComponentAt: (index: number | undefined) => void;
	unhighlightAll: () => void;
	getSelectedIndex: () => number | undefined;
}

/**
 * Legacy class implementation. Retained behind the
 * `search_refactor_keyboard_highlight_provider` feature gate while the
 * function-component replacement is rolled out. The exported
 * {@link KeyboardHighlightProvider} switches between the two.
 */
class KeyboardHighlightProviderClass extends React.Component<KeyboardHighlightProviderProps> {
	private currentlySelected: number | undefined = undefined;
	private currentSelectedTrackingId: string | null = null;

	private keyboardNavComponents: KeyboardNavComponent[] = [];

	private registerKeyboardEnabled = (
		currentId: number | null,
		domRef: HTMLElement,
		onHighlightChanged: OnHighlightChanged,
		onKeyDownWhenHighlighted: OnKeyDown | undefined,
		navigationTrackingId: string | null,
		onHighlightedCallback?: (index: number) => void,
	) => {
		const indexAt = this.keyboardNavComponents.findIndex(({ id }) => id === currentId);
		// If the currentId is null or we can't find any records of the id we will create a new entry
		const isNew: boolean = currentId === null || currentId === undefined || indexAt === -1;
		const id: number = isNew
			? Math.max(...this.keyboardNavComponents.map((component) => component.id), 0) + 1
			: this.keyboardNavComponents[indexAt].id;

		if (isNew) {
			this.keyboardNavComponents.push({
				id,
				domRef,
				onHighlightChanged,
				onKeyDownWhenHighlighted,
				navigationTrackingId,
				onHighlightedCallback,
			});
		} else {
			// Otherwise we will update the current entry
			this.keyboardNavComponents[indexAt] = {
				id,
				domRef,
				onHighlightChanged,
				onKeyDownWhenHighlighted,
				navigationTrackingId,
				onHighlightedCallback,
			};
		}

		this.keyboardNavComponents = this.keyboardNavComponents.sort((c1, c2) => {
			const result = c1.domRef.compareDocumentPosition(c2.domRef);
			if (
				result & Node.DOCUMENT_POSITION_FOLLOWING ||
				result & Node.DOCUMENT_POSITION_CONTAINED_BY
			) {
				return -1;
			} else if (
				result & Node.DOCUMENT_POSITION_PRECEDING ||
				result & Node.DOCUMENT_POSITION_CONTAINS
			) {
				return 1;
			}

			return 0;
		});

		const indexToHighlight = this.getSearchIdIndex(this.currentSelectedTrackingId);

		if (indexToHighlight !== undefined && indexToHighlight >= 0) {
			this.unhighlightAll();
			this.highlightComponentAt(indexToHighlight);
		}

		return id;
	};

	private unregisterKeyboardEnabled = (unregisterRef: number) => {
		if (this.currentlySelected === unregisterRef) {
			this.currentlySelected = undefined;
		}

		this.keyboardNavComponents = this.keyboardNavComponents.filter(
			({ id: ref }) => ref !== unregisterRef,
		);

		// Reset currentSelectedTrackingId when all components are unregistered
		if (this.keyboardNavComponents.length === 0) {
			this.currentSelectedTrackingId = null;
		}
	};

	onKeyDownCallbackAt = (index: number | undefined, e: KeyboardEvent): void => {
		if (typeof index !== 'undefined' && index >= 0 && index < this.keyboardNavComponents.length) {
			const onKeyDown = this.keyboardNavComponents[index].onKeyDownWhenHighlighted;
			onKeyDown && onKeyDown(e, this.keyboardNavComponents[index].domRef);
		}
	};

	onHighlightedCallback = (index: number): void => {
		if (typeof index !== 'undefined' && index >= 0 && index < this.keyboardNavComponents.length) {
			const callback = this.keyboardNavComponents[index].onHighlightedCallback;
			callback && callback(index);
		}
	};

	unhighlightComponentAt = (index: number | undefined): void => {
		if (typeof index !== 'undefined' && index >= 0 && index < this.keyboardNavComponents.length) {
			this.keyboardNavComponents[index].onHighlightChanged(false);
		}
	};

	highlightComponentAt = (index: number): void => {
		if (index >= 0 && index < this.keyboardNavComponents.length) {
			const { id, navigationTrackingId } = this.keyboardNavComponents[index];
			this.currentlySelected = id;
			this.currentSelectedTrackingId = navigationTrackingId;
			this.keyboardNavComponents[index].onHighlightChanged(true);
		}
	};

	unhighlightAll = (): void => {
		this.currentSelectedTrackingId = null;
		this.currentlySelected = undefined;

		this.keyboardNavComponents.forEach((component) => component.onHighlightChanged(false));
	};

	getSelectedIndex = (): number | undefined => {
		if (!this.currentlySelected) {
			return undefined;
		}

		return this.keyboardNavComponents.findIndex(({ id }) => id === this.currentlySelected);
	};

	getSearchIdIndex = (searchId: string | null): number | undefined =>
		searchId === null
			? undefined
			: this.keyboardNavComponents.findIndex(
					({ navigationTrackingId }) => navigationTrackingId === searchId,
			  );

	onKeyDown = (e: KeyboardEvent): void => {
		if (this.keyboardNavComponents.length === 0) {
			return;
		}

		if (e.isComposing || e.keyCode === 229) {
			return;
		}

		let currentIndex = this.getSelectedIndex();
		const prevIndex = currentIndex;

		if (e.code === 'ArrowDown' || e.keyCode === 40) {
			if (!e.shiftKey) {
				if (typeof currentIndex === 'undefined') {
					currentIndex = 0;
				} else {
					currentIndex = (currentIndex + 1) % this.keyboardNavComponents.length;
				}
				e.preventDefault();
			}
		} else if (e.code === 'ArrowUp' || e.keyCode === 38) {
			if (!e.shiftKey) {
				if (typeof currentIndex === 'undefined') {
					currentIndex = this.keyboardNavComponents.length - 1;
				} else {
					currentIndex -= 1;

					if (currentIndex < 0) {
						currentIndex = this.keyboardNavComponents.length - Math.abs(currentIndex);
					}
				}
				e.preventDefault();
			}
		} else {
			if (typeof currentIndex !== 'undefined') {
				this.onKeyDownCallbackAt(currentIndex, e);
			}
			return;
		}

		if (prevIndex !== currentIndex && currentIndex !== undefined) {
			this.unhighlightComponentAt(prevIndex);
			this.highlightComponentAt(currentIndex);
			this.onHighlightedCallback(currentIndex);
		}
	};

	attach = (listenerNode: HTMLElement | undefined | null): void => {
		if (listenerNode) {
			listenerNode.addEventListener('keydown', this.onKeyDown);
		}
	};

	detach = (listenerNode: HTMLElement | undefined | null): void => {
		if (listenerNode) {
			listenerNode.removeEventListener('keydown', this.onKeyDown);
		}
	};

	componentDidMount(): void {
		this.attach(this.props.listenerNode);
	}

	componentDidUpdate(prevProps: KeyboardHighlightProviderProps): void {
		if (prevProps.listenerNode !== this.props.listenerNode) {
			this.detach(prevProps.listenerNode);
			this.attach(this.props.listenerNode);
		}
	}

	componentWillUnmount(): void {
		this.detach(this.props.listenerNode);
	}

	render(): React.JSX.Element {
		return (
			<KeyboardHighlightContext.Provider
				value={{
					registerKeyboardEnabled: this.registerKeyboardEnabled,
					unregisterKeyboardEnabled: this.unregisterKeyboardEnabled,
					getSelectedIndex: this.getSelectedIndex,
					highlightComponentAt: this.highlightComponentAt,
					unhighlightComponentAt: this.unhighlightComponentAt,
					unhighlightAll: this.unhighlightAll,
					keyboardNavComponents: this.keyboardNavComponents,
				}}
			>
				{this.props.children}
			</KeyboardHighlightContext.Provider>
		);
	}
}

/**
 * Function-component implementation of `KeyboardHighlightProvider`.
 */
const KeyboardHighlightProviderFn = React.forwardRef<
	KeyboardHighlightProviderHandle,
	KeyboardHighlightProviderProps
>(({ children, listenerNode }, ref): React.JSX.Element => {
	const currentlySelectedRef = useRef<number | undefined>(undefined);
	const currentSelectedTrackingIdRef = useRef<string | null>(null);
	const keyboardNavComponentsRef = useRef<KeyboardNavComponent[]>([]);

	const getSelectedIndex = useCallback((): number | undefined => {
		const currentlySelected = currentlySelectedRef.current;
		if (!currentlySelected) {
			return undefined;
		}
		return keyboardNavComponentsRef.current.findIndex(({ id }) => id === currentlySelected);
	}, []);

	const getSearchIdIndex = useCallback(
		(searchId: string | null): number | undefined =>
			searchId === null
				? undefined
				: keyboardNavComponentsRef.current.findIndex(
						({ navigationTrackingId }) => navigationTrackingId === searchId,
				  ),
		[],
	);

	const unhighlightComponentAt = useCallback((index: number | undefined): void => {
		const components = keyboardNavComponentsRef.current;
		if (typeof index !== 'undefined' && index >= 0 && index < components.length) {
			components[index].onHighlightChanged(false);
		}
	}, []);

	const highlightComponentAt = useCallback((index: number): void => {
		const components = keyboardNavComponentsRef.current;
		if (index >= 0 && index < components.length) {
			const { id, navigationTrackingId } = components[index];
			currentlySelectedRef.current = id;
			currentSelectedTrackingIdRef.current = navigationTrackingId;
			components[index].onHighlightChanged(true);
		}
	}, []);

	const unhighlightAll = useCallback((): void => {
		currentSelectedTrackingIdRef.current = null;
		currentlySelectedRef.current = undefined;
		keyboardNavComponentsRef.current.forEach((component) => component.onHighlightChanged(false));
	}, []);

	const onHighlightedCallback = useCallback((index: number): void => {
		const components = keyboardNavComponentsRef.current;
		if (typeof index !== 'undefined' && index >= 0 && index < components.length) {
			const callback = components[index].onHighlightedCallback;
			callback && callback(index);
		}
	}, []);

	const onKeyDownCallbackAt = useCallback((index: number | undefined, e: KeyboardEvent): void => {
		const components = keyboardNavComponentsRef.current;
		if (typeof index !== 'undefined' && index >= 0 && index < components.length) {
			const onKeyDown = components[index].onKeyDownWhenHighlighted;
			onKeyDown && onKeyDown(e, components[index].domRef);
		}
	}, []);

	const registerKeyboardEnabled = useCallback<RegisterKeyboardEnabled<HTMLElement>>(
		(
			currentId,
			domRef,
			onHighlightChanged,
			onKeyDownWhenHighlighted,
			navigationTrackingId,
			onHighlightedCb,
		) => {
			const components = keyboardNavComponentsRef.current;
			const indexAt = components.findIndex(({ id }) => id === currentId);
			// If the currentId is null or we can't find any records of the id we will create a new entry
			const isNew: boolean = currentId === null || currentId === undefined || indexAt === -1;
			const id: number = isNew
				? Math.max(...components.map((component) => component.id), 0) + 1
				: components[indexAt].id;

			if (isNew) {
				components.push({
					id,
					domRef,
					onHighlightChanged,
					onKeyDownWhenHighlighted,
					navigationTrackingId,
					onHighlightedCallback: onHighlightedCb,
				});
			} else {
				// Otherwise we will update the current entry
				components[indexAt] = {
					id,
					domRef,
					onHighlightChanged,
					onKeyDownWhenHighlighted,
					navigationTrackingId,
					onHighlightedCallback: onHighlightedCb,
				};
			}

			keyboardNavComponentsRef.current = components.sort((c1, c2) => {
				const result = c1.domRef.compareDocumentPosition(c2.domRef);
				if (
					result & Node.DOCUMENT_POSITION_FOLLOWING ||
					result & Node.DOCUMENT_POSITION_CONTAINED_BY
				) {
					return -1;
				} else if (
					result & Node.DOCUMENT_POSITION_PRECEDING ||
					result & Node.DOCUMENT_POSITION_CONTAINS
				) {
					return 1;
				}
				return 0;
			});

			const indexToHighlight = getSearchIdIndex(currentSelectedTrackingIdRef.current);

			if (indexToHighlight !== undefined && indexToHighlight >= 0) {
				unhighlightAll();
				highlightComponentAt(indexToHighlight);
			}

			return id;
		},
		[getSearchIdIndex, highlightComponentAt, unhighlightAll],
	);

	const unregisterKeyboardEnabled = useCallback((unregisterRef: number): void => {
		if (currentlySelectedRef.current === unregisterRef) {
			currentlySelectedRef.current = undefined;
		}

		keyboardNavComponentsRef.current = keyboardNavComponentsRef.current.filter(
			({ id: ref }) => ref !== unregisterRef,
		);

		// Reset currentSelectedTrackingId when all components are unregistered
		if (keyboardNavComponentsRef.current.length === 0) {
			currentSelectedTrackingIdRef.current = null;
		}
	}, []);

	const onKeyDown = useCallback(
		(e: KeyboardEvent): void => {
			const components = keyboardNavComponentsRef.current;
			if (components.length === 0) {
				return;
			}

			if (e.isComposing || e.keyCode === 229) {
				return;
			}

			let currentIndex = getSelectedIndex();
			const prevIndex = currentIndex;

			if (e.code === 'ArrowDown' || e.keyCode === 40) {
				if (!e.shiftKey) {
					if (typeof currentIndex === 'undefined') {
						currentIndex = 0;
					} else {
						currentIndex = (currentIndex + 1) % components.length;
					}
					e.preventDefault();
				}
			} else if (e.code === 'ArrowUp' || e.keyCode === 38) {
				if (!e.shiftKey) {
					if (typeof currentIndex === 'undefined') {
						currentIndex = components.length - 1;
					} else {
						currentIndex -= 1;

						if (currentIndex < 0) {
							currentIndex = components.length - Math.abs(currentIndex);
						}
					}
					e.preventDefault();
				}
			} else {
				if (typeof currentIndex !== 'undefined') {
					onKeyDownCallbackAt(currentIndex, e);
				}
				return;
			}

			if (prevIndex !== currentIndex && currentIndex !== undefined) {
				unhighlightComponentAt(prevIndex);
				highlightComponentAt(currentIndex);
				onHighlightedCallback(currentIndex);
			}
		},
		[
			getSelectedIndex,
			onKeyDownCallbackAt,
			unhighlightComponentAt,
			highlightComponentAt,
			onHighlightedCallback,
		],
	);

	useEffect(() => {
		if (!listenerNode) {
			return;
		}
		listenerNode.addEventListener('keydown', onKeyDown);
		return () => {
			listenerNode.removeEventListener('keydown', onKeyDown);
		};
	}, [listenerNode, onKeyDown]);

	const contextValue = useMemo<KeyboardContextProps<HTMLElement>>(
		() => ({
			registerKeyboardEnabled,
			unregisterKeyboardEnabled,
			getSelectedIndex,
			highlightComponentAt,
			unhighlightComponentAt,
			unhighlightAll,
			get keyboardNavComponents() {
				// Always read the latest registry from the ref so consumers that
				// destructure `keyboardNavComponents` from the context still see
				// up-to-date data without forcing a re-render of the provider.
				return keyboardNavComponentsRef.current;
			},
		}),
		[
			registerKeyboardEnabled,
			unregisterKeyboardEnabled,
			getSelectedIndex,
			highlightComponentAt,
			unhighlightComponentAt,
			unhighlightAll,
		],
	);

	useImperativeHandle(ref, () => ({
		highlightComponentAt,
		unhighlightComponentAt,
		unhighlightAll,
		getSelectedIndex,
	}));

	return (
		<KeyboardHighlightContext.Provider value={contextValue}>
			{children}
		</KeyboardHighlightContext.Provider>
	);
});

/**
 * Public `KeyboardHighlightProvider`. Switches between the legacy class
 * implementation and the new function-component implementation
 *
 * Remove when cleaning up `search_refactor_keyboard_highlight_provider` feature gate.
 */
export const KeyboardHighlightProvider: React.ForwardRefExoticComponent<
	KeyboardHighlightProviderProps & React.RefAttributes<KeyboardHighlightProviderHandle>
> = React.forwardRef<KeyboardHighlightProviderHandle, KeyboardHighlightProviderProps>(
	(props, ref): React.JSX.Element => {
		if (fg('search_refactor_keyboard_highlight_provider')) {
			return <KeyboardHighlightProviderFn {...props} ref={ref} />;
		}
		return <KeyboardHighlightProviderClass {...props} />;
	},
);

/**
 * Returns the keyboard-highlight context provided by the nearest
 *
 * This hook might return 'undefined' during SSR
 */
export const useKeyboardHighlightProvider: () =>
	| KeyboardContextProps<HTMLElement>
	| undefined = () => {
	return useContext(KeyboardHighlightContext);
};
